import * as R from 'ramda'
import {
  EquipmentCategory,
  Opportunity,
  OpportunityBuilding,
  OpportunityContract,
  EquipmentContract,
  EquipmentSfId,
  OpportunityCategory
} from '../../types/types'

interface NemoAccount {
  opportunities: Opportunity[]
}

export const maxEquipments = 400
export const minEquipments = 150

export const getOpportunityIds = (accounts: NemoAccount[]) =>
  R.chain(({ opportunities }) => opportunities.map(({ id }) => id), accounts)

export const getElevatorCountForOpportunity = (buildings: OpportunityBuilding[]): number =>
  buildings.map(getBuildingElevatorCount).reduce(R.add, 0)

export const getEscalatorCountForOpportunity = (buildings: OpportunityBuilding[]): number =>
  buildings.map(getBuildingEscalatorCount).reduce(R.add, 0)

export const getDoorCountForOpportunity = (buildings: OpportunityBuilding[]) =>
  buildings.map(getBuildingDoorCount).reduce(R.add, 0)

export const getPeopleflowCountForOpportunity = (buildings: OpportunityBuilding[]) =>
  buildings.map(getBuildingPeopleflowCount).reduce(R.add, 0)

const getBuildingElevatorCount = (building: OpportunityBuilding): number =>
  getEquipmentCountByEquipmentCategory(building, 'elevator')

const getBuildingEscalatorCount = (building: OpportunityBuilding): number =>
  getEquipmentCountByEquipmentCategory(building, 'escalator')

const getBuildingDoorCount = (building: OpportunityBuilding) => getEquipmentCountByEquipmentCategory(building, 'door')

const getBuildingPeopleflowCount = (building: OpportunityBuilding) =>
  getEquipmentCountByEquipmentCategory(building, 'resiflow')

const getEquipmentCountByEquipmentCategory = (
  building: OpportunityBuilding,
  equipmentCategory: EquipmentCategory
): number => {
  const equipment = R.propOr([], 'equipment', building)
  return equipment.filter(e => e.equipmentCategory === equipmentCategory).length
}

export const hasContracts = (opportunity: Opportunity): boolean =>
  Array.isArray(opportunity.contracts) && opportunity.contracts.length > 0

const getContracts = (opportunity: Opportunity): OpportunityContract[] =>
  opportunity.contracts && hasContracts(opportunity) ? opportunity.contracts : []

export const getPrimaryContract = (opportunity: Opportunity): OpportunityContract | undefined =>
  opportunity.contracts && hasContracts(opportunity) ? opportunity.contracts[0] : undefined

const getOpportunityContractBuildings = (opportunity: Opportunity) =>
  getContracts(opportunity).map(contract => Object.values(contract.buildings))

const flattenTwice = (a: any[], cb: (item: any) => any) =>
  a.reduce(
    (acc: any, i: any) =>
      Object.assign(
        {},
        acc,
        i.reduce((acc2: any, i2: any) => Object.assign({}, acc2, cb(i2)), {})
      ),
    {}
  )

export const previousContractEquipment = (opportunity: Opportunity): Record<EquipmentSfId, EquipmentContract> =>
  flattenTwice(getOpportunityContractBuildings(opportunity), (i: Record<string, EquipmentContract>) => i)

export const isDoorsOpportunity = (opportunity: Opportunity): boolean => opportunity.businessType === 'Doors'

export function offerHasOldContracts(contracts: OpportunityContract[] | undefined) {
  return contracts != null && contracts.length > 0 && contracts[0].id.startsWith('a0w')
}

export function translateNewContractSapNumber(newContractName: string) {
  return /^[a-z]/i.test(newContractName) ? newContractName : newContractName.replace(/^00/, '')
}

export function truncateOpportunityName(opportunity: Opportunity, offset: number): string {
  const { name: oppName } = opportunity
  return (oppName || '').substr(0, offset)
}

export const isDoorCategory = (opportunity: Opportunity): boolean => isDoorsOpportunity(opportunity) // && opportunity.opportunityCategory === 'SDB Renegotiation'

export const isRenegoOpp = (opportunityCategory: OpportunityCategory): boolean =>
  opportunityCategory === 'Renegotiation'
